const VOID_HTML_TAGS = new Set([
  "area",
  "base",
  "br",
  "col",
  "embed",
  "hr",
  "img",
  "input",
  "link",
  "meta",
  "param",
  "source",
  "track",
  "wbr",
]);

/**
 * Cuts `html` after `maxWords` whitespace-delimited words in text nodes,
 * then appends closing tags.
 * Matches animation steps (one step per raw markdown word) to visible words
 */
export const truncateHtmlByVisibleWordCount = (
  html: string,
  maxWords: number
): string => {
  if (maxWords <= 0 || !html) {
    return "";
  }

  let index = 0;
  let visibleWordCount = 0;
  let insideWord = false;
  const openTagsStack: string[] = [];
  let truncatedOutput = "";

  const isWhitespace = (character: string): boolean => /\s/.test(character);

  const finishWordIfNeeded = (): void => {
    if (insideWord) {
      insideWord = false;
      visibleWordCount += 1;
    }
  };

  while (index < html.length) {
    if (visibleWordCount >= maxWords) {
      break;
    }

    if (html[index] === "<") {
      finishWordIfNeeded();
      if (visibleWordCount >= maxWords) {
        break;
      }
      const closingBracketIndex = html.indexOf(">", index);
      if (closingBracketIndex === -1) {
        break;
      }
      const fullTag = html.slice(index, closingBracketIndex + 1);
      truncatedOutput += fullTag;

      const tagInnerContent = fullTag.slice(1, -1).trim();
      if (tagInnerContent.startsWith("!") || tagInnerContent.startsWith("?")) {
        index = closingBracketIndex + 1;
        continue;
      }

      const isClosingTag = tagInnerContent.startsWith("/");
      const isSelfClosingTag = /\/\s*$/.test(tagInnerContent);
      const tagNameMatch = tagInnerContent.match(/^\/?\s*([\w:-]+)/);
      const extractedTagName = tagNameMatch ? tagNameMatch[1] : "";
      const normalizedTagName = extractedTagName.toLowerCase();

      if (isClosingTag) {
        if (
          openTagsStack.length > 0 &&
          openTagsStack[openTagsStack.length - 1] === normalizedTagName
        ) {
          openTagsStack.pop();
        } else {
          const matchingOpenTagIndex = openTagsStack.lastIndexOf(
            normalizedTagName
          );
          if (matchingOpenTagIndex !== -1) {
            openTagsStack.splice(matchingOpenTagIndex);
          }
        }
      } else if (!isSelfClosingTag && !VOID_HTML_TAGS.has(normalizedTagName)) {
        openTagsStack.push(normalizedTagName);
      }

      index = closingBracketIndex + 1;
    } else {
      const character = html[index];
      if (isWhitespace(character)) {
        finishWordIfNeeded();
        if (visibleWordCount >= maxWords) {
          break;
        }
        truncatedOutput += character;
        index += 1;
      } else {
        if (!insideWord && visibleWordCount >= maxWords) {
          break;
        }
        insideWord = true;
        truncatedOutput += character;
        index += 1;
      }
    }
  }

  finishWordIfNeeded();

  for (
    let stackIndex = openTagsStack.length - 1;
    stackIndex >= 0;
    stackIndex -= 1
  ) {
    truncatedOutput += `</${openTagsStack[stackIndex]}>`;
  }

  return truncatedOutput;
};
